/**
 * MuscleBodyMap — Interactive Anatomical SVG Diagram
 *
 * Front + back views side by side.
 * ViewBox: "0 0 240 332"
 *   Front figure: center x=60
 *   Back  figure: center x=180
 */

import { useState, useCallback } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import { computeCurrentFatigue, setMuscleInjured } from '@/hooks/useFatigue';
import { getMuscleWeeklyTarget } from '@/hooks/useWeeklyVolume';
import type { MuscleGroup, MuscleState } from '@/types';

// ── Color helpers ─────────────────────────────────────────────

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * Math.min(1, Math.max(0, t));
}

function fatigueToColor(fatigue: number, injured: boolean, hasData: boolean): string {
  if (!hasData) return '#2e2e2e';
  if (injured)  return '#7c3aed';
  if (fatigue < 0.01) return '#22c55e';
  if (fatigue <= 0.5) {
    const t = fatigue / 0.5;
    return `rgb(${~~lerp(34,245,t)},${~~lerp(197,158,t)},${~~lerp(94,11,t)})`;
  }
  const t = (fatigue - 0.5) / 0.5;
  return `rgb(${~~lerp(245,239,t)},${~~lerp(158,68,t)},${~~lerp(11,68,t)})`;
}

// ── Muscle region definitions ─────────────────────────────────

interface RegionDef {
  muscle: MuscleGroup;
  label: string;
  shapes: Array<{
    el: 'ellipse' | 'rect' | 'path' | 'circle';
    props: Record<string, string | number>;
  }>;
}

// ── Front view regions (center x=60) ─────────────────────────

const FRONT_REGIONS: RegionDef[] = [
  {
    muscle: 'front_delt',
    label: 'Front Delt',
    shapes: [
      { el: 'path', props: { d: 'M 27,52 C 20,56 18,66 22,74 C 26,80 34,82 38,76 C 41,70 40,60 36,54 C 33,50 30,50 27,52 Z' } },
      { el: 'path', props: { d: 'M 93,52 C 100,56 102,66 98,74 C 94,80 86,82 82,76 C 79,70 80,60 84,54 C 87,50 90,50 93,52 Z' } },
    ],
  },
  {
    muscle: 'lateral_delt',
    label: 'Lateral Delt',
    shapes: [
      { el: 'ellipse', props: { cx: 19, cy: 66, rx: 5, ry: 9 } },
      { el: 'ellipse', props: { cx: 101, cy: 66, rx: 5, ry: 9 } },
    ],
  },
  {
    muscle: 'chest',
    label: 'Chest',
    shapes: [
      { el: 'path', props: { d: 'M 36,58 C 30,64 30,80 34,90 C 38,98 46,102 54,98 C 58,92 58,80 56,68 C 53,60 46,56 40,56 Z' } },
      { el: 'path', props: { d: 'M 84,58 C 90,64 90,80 86,90 C 82,98 74,102 66,98 C 62,92 62,80 64,68 C 67,60 74,56 80,56 Z' } },
    ],
  },
  {
    muscle: 'biceps',
    label: 'Biceps',
    shapes: [
      { el: 'path', props: { d: 'M 22,60 C 16,68 14,84 16,100 C 18,112 24,120 30,122 C 33,114 33,98 31,84 C 29,70 27,60 22,60 Z' } },
      { el: 'path', props: { d: 'M 98,60 C 104,68 106,84 104,100 C 102,112 96,120 90,122 C 87,114 87,98 89,84 C 91,70 93,60 98,60 Z' } },
    ],
  },
  {
    muscle: 'forearms',
    label: 'Forearms',
    shapes: [
      { el: 'path', props: { d: 'M 18,134 C 13,142 13,162 17,176 C 20,184 26,190 31,190 L 34,186 C 32,176 30,160 30,146 C 30,138 28,132 24,132 Z' } },
      { el: 'path', props: { d: 'M 102,134 C 107,142 107,162 103,176 C 100,184 94,190 89,190 L 86,186 C 88,176 90,160 90,146 C 90,138 92,132 96,132 Z' } },
    ],
  },
  {
    muscle: 'core',
    label: 'Core',
    shapes: [
      { el: 'path', props: { d: 'M 53,100 C 51,108 51,130 53,142 C 55,148 58,148 60,146 L 60,98 C 58,96 55,96 53,100 Z' } },
      { el: 'path', props: { d: 'M 67,100 C 69,108 69,130 67,142 C 65,148 62,148 60,146 L 60,98 C 62,96 65,96 67,100 Z' } },
    ],
  },
  {
    muscle: 'hip_flexors',
    label: 'Hip Flexors',
    shapes: [
      { el: 'ellipse', props: { cx: 50, cy: 160, rx: 8, ry: 7 } },
      { el: 'ellipse', props: { cx: 70, cy: 160, rx: 8, ry: 7 } },
    ],
  },
  {
    muscle: 'quads',
    label: 'Quads',
    shapes: [
      { el: 'path', props: { d: 'M 34,180 C 27,188 25,212 27,236 C 29,250 37,260 46,263 C 52,258 54,246 52,230 C 50,212 46,194 46,180 C 44,174 38,174 34,180 Z' } },
      { el: 'path', props: { d: 'M 86,180 C 93,188 95,212 93,236 C 91,250 83,260 74,263 C 68,258 66,246 68,230 C 70,212 74,194 74,180 C 76,174 82,174 86,180 Z' } },
    ],
  },
  {
    muscle: 'calves',
    label: 'Calves',
    shapes: [
      { el: 'path', props: { d: 'M 45,268 C 40,278 40,298 44,312 C 47,320 52,324 57,323 L 59,320 C 59,310 57,294 57,280 C 57,270 55,264 51,264 Z' } },
      { el: 'path', props: { d: 'M 75,268 C 80,278 80,298 76,312 C 73,320 68,324 63,323 L 61,320 C 61,310 63,294 63,280 C 63,270 65,264 69,264 Z' } },
    ],
  },
];

// ── Back view regions (center x=180) ─────────────────────────

const BACK_REGIONS: RegionDef[] = [
  {
    muscle: 'traps',
    label: 'Traps',
    shapes: [
      { el: 'path', props: { d: 'M 180,46 C 194,46 204,54 204,64 C 204,76 194,82 180,84 C 166,82 156,76 156,64 C 156,54 166,46 180,46 Z' } },
    ],
  },
  {
    muscle: 'rear_delt',
    label: 'Rear Delt',
    shapes: [
      { el: 'path', props: { d: 'M 147,52 C 140,56 138,66 142,74 C 146,80 154,82 158,76 C 161,70 160,60 156,54 C 153,50 150,50 147,52 Z' } },
      { el: 'path', props: { d: 'M 213,52 C 220,56 222,66 218,74 C 214,80 206,82 202,76 C 199,70 200,60 204,54 C 207,50 210,50 213,52 Z' } },
    ],
  },
  {
    muscle: 'triceps',
    label: 'Triceps',
    shapes: [
      { el: 'path', props: { d: 'M 143,60 C 136,68 134,86 136,102 C 138,114 145,122 151,124 C 154,116 154,100 152,86 C 150,72 147,60 143,60 Z' } },
      { el: 'path', props: { d: 'M 217,60 C 224,68 226,86 224,102 C 222,114 215,122 209,124 C 206,116 206,100 208,86 C 210,72 213,60 217,60 Z' } },
    ],
  },
  {
    muscle: 'lats',
    label: 'Lats',
    shapes: [
      { el: 'path', props: { d: 'M 158,62 C 148,70 144,88 146,108 C 148,124 156,134 164,136 C 168,126 168,110 166,96 C 164,82 162,70 166,64 Z' } },
      { el: 'path', props: { d: 'M 202,62 C 212,70 216,88 214,108 C 212,124 204,134 196,136 C 192,126 192,110 194,96 C 196,82 198,70 194,64 Z' } },
    ],
  },
  {
    muscle: 'rhomboids',
    label: 'Rhomboids',
    shapes: [
      { el: 'path', props: { d: 'M 180,68 C 186,72 190,80 186,88 C 183,93 177,93 174,88 C 170,80 174,72 180,68 Z' } },
    ],
  },
  {
    muscle: 'lower_back',
    label: 'Lower Back',
    shapes: [
      { el: 'path', props: { d: 'M 167,124 C 162,128 162,142 167,148 C 171,152 176,152 180,150 C 184,152 189,152 193,148 C 198,142 198,128 193,124 C 189,118 184,118 180,120 C 176,118 171,118 167,124 Z' } },
    ],
  },
  {
    muscle: 'glutes',
    label: 'Glutes',
    shapes: [
      { el: 'path', props: { d: 'M 150,152 C 142,158 140,174 146,184 C 152,192 163,196 172,190 C 178,182 178,168 174,160 C 168,152 157,148 150,152 Z' } },
      { el: 'path', props: { d: 'M 210,152 C 218,158 220,174 214,184 C 208,192 197,196 188,190 C 182,182 182,168 186,160 C 192,152 203,148 210,152 Z' } },
    ],
  },
  {
    muscle: 'hamstrings',
    label: 'Hamstrings',
    shapes: [
      { el: 'path', props: { d: 'M 156,180 C 148,188 146,212 148,236 C 150,250 158,260 167,263 C 172,258 174,246 172,230 C 170,212 166,194 166,180 C 164,174 158,174 156,180 Z' } },
      { el: 'path', props: { d: 'M 204,180 C 212,188 214,212 212,236 C 210,250 202,260 193,263 C 188,258 186,246 188,230 C 190,212 194,194 194,180 C 196,174 202,174 204,180 Z' } },
    ],
  },
  {
    muscle: 'calves',
    label: 'Calves',
    shapes: [
      { el: 'path', props: { d: 'M 160,268 C 154,278 154,300 158,314 C 162,322 168,326 173,325 L 175,322 C 175,312 173,296 173,280 C 173,270 171,264 167,264 Z' } },
      { el: 'path', props: { d: 'M 200,268 C 206,278 206,300 202,314 C 198,322 192,326 187,325 L 185,322 C 185,312 187,296 187,280 C 187,270 189,264 193,264 Z' } },
    ],
  },
];

// ── Body silhouette (used for clip path + fill + outline) ─────
// c = center x of the figure

function bodyShapes(c: number) {
  return [
    // Head — proportional oval (smaller relative to body)
    <ellipse key="head" cx={c} cy={18} rx={12} ry={14} />,

    // Neck — tapers from jaw to shoulders
    <path key="neck" d={`M ${c-6},31 Q ${c},29 ${c+6},31 L ${c+5},45 Q ${c},47 ${c-5},45 Z`} />,

    // Torso — shoulder flare → armpit inset → waist taper → hip flare
    <path key="torso" d={`
      M ${c-8},45
      C ${c-20},45 ${c-30},48 ${c-32},58
      C ${c-34},68 ${c-28},80 ${c-26},96
      C ${c-23},112 ${c-19},126 ${c-16},138
      C ${c-14},148 ${c-18},156 ${c-22},160
      Q ${c},164 ${c+22},160
      C ${c+18},156 ${c+14},148 ${c+16},138
      C ${c+19},126 ${c+23},112 ${c+26},96
      C ${c+28},80 ${c+34},68 ${c+32},58
      C ${c+30},48 ${c+20},45 ${c+8},45 Z
    `} />,

    // Left upper arm — tapers from shoulder to elbow
    <path key="la" d={`
      M ${c-34},52
      C ${c-42},58 ${c-44},76 ${c-42},94
      C ${c-40},108 ${c-34},120 ${c-27},128
      L ${c-22},124
      C ${c-22},108 ${c-22},88 ${c-22},72
      C ${c-22},60 ${c-24},52 ${c-28},52 Z
    `} />,

    // Right upper arm — mirror
    <path key="ra" d={`
      M ${c+34},52
      C ${c+42},58 ${c+44},76 ${c+42},94
      C ${c+40},108 ${c+34},120 ${c+27},128
      L ${c+22},124
      C ${c+22},108 ${c+22},88 ${c+22},72
      C ${c+22},60 ${c+24},52 ${c+28},52 Z
    `} />,

    // Left forearm — tapers to wrist
    <path key="lf" d={`
      M ${c-30},130
      C ${c-37},138 ${c-37},158 ${c-33},174
      C ${c-30},184 ${c-24},192 ${c-18},193
      L ${c-14},189
      C ${c-16},178 ${c-16},162 ${c-16},148
      C ${c-16},138 ${c-18},132 ${c-22},130 Z
    `} />,

    // Right forearm — mirror
    <path key="rf" d={`
      M ${c+30},130
      C ${c+37},138 ${c+37},158 ${c+33},174
      C ${c+30},184 ${c+24},192 ${c+18},193
      L ${c+14},189
      C ${c+16},178 ${c+16},162 ${c+16},148
      C ${c+16},138 ${c+18},132 ${c+22},130 Z
    `} />,

    // Hips / pelvis — bridges torso to thighs
    <path key="hips" d={`
      M ${c-22},158
      C ${c-28},160 ${c-30},170 ${c-26},176
      Q ${c},180 ${c+26},176
      C ${c+30},170 ${c+28},160 ${c+22},158
      Q ${c},156 ${c-22},158 Z
    `} />,

    // Left thigh — wide at hip, tapers toward knee
    <path key="lthi" d={`
      M ${c-26},174
      C ${c-33},182 ${c-35},204 ${c-33},228
      C ${c-31},242 ${c-23},254 ${c-14},258
      L ${c-8},254
      C ${c-9},240 ${c-9},220 ${c-8},202
      C ${c-7},186 ${c-2},176 ${c+2},174
      C ${c-5},170 ${c-18},170 ${c-26},174 Z
    `} />,

    // Right thigh — mirror
    <path key="rthi" d={`
      M ${c+26},174
      C ${c+33},182 ${c+35},204 ${c+33},228
      C ${c+31},242 ${c+23},254 ${c+14},258
      L ${c+8},254
      C ${c+9},240 ${c+9},220 ${c+8},202
      C ${c+7},186 ${c+2},176 ${c-2},174
      C ${c+5},170 ${c+18},170 ${c+26},174 Z
    `} />,

    // Left lower leg — calf belly bulge on outer edge
    <path key="lleg" d={`
      M ${c-17},260
      C ${c-24},270 ${c-24},290 ${c-18},306
      C ${c-14},316 ${c-8},322 ${c-2},323
      L ${c+2},320
      C ${c+2},310 ${c},294 ${c},278
      C ${c},266 ${c},260 ${c},258
      C ${c-4},256 ${c-12},256 ${c-17},260 Z
    `} />,

    // Right lower leg — mirror
    <path key="rleg" d={`
      M ${c+17},260
      C ${c+24},270 ${c+24},290 ${c+18},306
      C ${c+14},316 ${c+8},322 ${c+2},323
      L ${c-2},320
      C ${c-2},310 ${c},294 ${c},278
      C ${c},266 ${c},260 ${c},258
      C ${c+4},256 ${c+12},256 ${c+17},260 Z
    `} />,
  ];
}

function BodyFill({ cx }: { cx: number }) {
  return <g fill="#252525" stroke="none">{bodyShapes(cx)}</g>;
}

function BodyOutline({ cx }: { cx: number }) {
  return <g fill="none" stroke="#4a4a4a" strokeWidth="0.8" strokeLinejoin="round">{bodyShapes(cx)}</g>;
}

// ── Detail Sheet ──────────────────────────────────────────────

interface DetailSheetProps {
  muscle: MuscleGroup;
  label: string;
  state: MuscleState | undefined;
  fatigue: number;
  weeklyVol: number;
  onClose: () => void;
}

function DetailSheet({ muscle, label, state, fatigue, weeklyVol, onClose }: DetailSheetProps) {
  const settings = useLiveQuery(() => db.settings.get('singleton'), []);
  const targetSets = getMuscleWeeklyTarget(muscle, settings?.experienceLevel, settings?.trainingGoal);
  const recoveryPct = Math.round((1 - fatigue) * 100);
  const isInjured   = state?.isInjured ?? false;

  const hoursUntilReady = (() => {
    if (!state || fatigue <= 0.1) return 0;
    const k = state.decayConstantPerHour;
    if (k <= 0) return 0;
    return Math.max(0, Math.round(-Math.log(0.1 / fatigue) / k));
  })();

  const statusLabel =
    isInjured    ? 'Injured — excluded from generator' :
    fatigue < 0.1 ? 'Fully recovered' :
    fatigue < 0.4 ? 'Ready to train' :
    fatigue < 0.7 ? 'Partially recovered' :
                    'Fatigued — will be skipped';

  const statusColor =
    isInjured    ? 'text-accent' :
    fatigue < 0.1 ? 'text-fresh' :
    fatigue < 0.4 ? 'text-fresh' :
    fatigue < 0.7 ? 'text-partial' :
                    'text-fatigued';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center pb-safe">
      <button className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-surface-100 rounded-t-3xl
                      pt-4 pb-6 px-6 shadow-card-lg animate-slide-up">
        <div className="w-10 h-1 bg-surface-400 rounded-full mx-auto mb-5" />
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-xl font-bold text-ink">{label}</h3>
            <p className={`text-sm mt-0.5 font-medium ${statusColor}`}>{statusLabel}</p>
          </div>
          <button className="btn-ghost p-1 text-lg" onClick={onClose}>✕</button>
        </div>

        <div className="flex gap-3 mb-5">
          {[
            { label: 'Recovery',    value: `${recoveryPct}%` },
            { label: 'Weekly Sets', value: `${weeklyVol.toFixed(1)} / ${targetSets}` },
            { label: 'Ready in',    value: hoursUntilReady > 0 ? `${hoursUntilReady}h` : 'Now' },
          ].map(({ label: l, value }) => (
            <div key={l} className="flex-1 card text-center py-3">
              <p className="text-lg font-bold text-ink">{value}</p>
              <p className="text-xs text-ink-muted mt-0.5">{l}</p>
            </div>
          ))}
        </div>

        <div className="mb-5">
          <div className="flex justify-between text-xs text-ink-muted mb-1.5">
            <span>Fatigued</span>
            <span>Recovered</span>
          </div>
          <div className="h-2.5 bg-surface-400 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${recoveryPct}%`, background: fatigueToColor(fatigue, isInjured, true) }}
            />
          </div>
        </div>

        <div className="flex items-center justify-between bg-surface-200 rounded-2xl px-4 py-3.5">
          <div>
            <p className="text-sm font-medium text-ink">Mark as Injured</p>
            <p className="text-xs text-ink-muted mt-0.5">
              Excludes this muscle from all generated workouts
            </p>
          </div>
          <button
            role="switch"
            aria-checked={isInjured}
            onClick={() => setMuscleInjured(muscle, !isInjured)}
            className={`w-12 h-7 rounded-full flex items-center px-0.5
                        transition-colors duration-200 active:scale-95 min-w-tap
                        ${isInjured ? 'bg-accent' : 'bg-surface-400'}`}
          >
            <div className={`w-6 h-6 rounded-full bg-white shadow transition-transform duration-200
                             ${isInjured ? 'translate-x-5' : 'translate-x-0'}`} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── SVG Region Renderer ───────────────────────────────────────

function MuscleRegion({
  region, color, isSelected, onClick,
}: {
  region: RegionDef;
  color: string;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }} opacity={isSelected ? 1 : 0.85}>
      {region.shapes.map((shape, i) => {
        const shared = {
          key: i,
          fill: color,
          stroke: isSelected ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.25)',
          strokeWidth: isSelected ? 1.0 : 0.3,
          style: { transition: 'fill 0.5s ease' },
        };
        if (shape.el === 'ellipse') return <ellipse {...shared} {...(shape.props as React.SVGProps<SVGEllipseElement>)} />;
        if (shape.el === 'rect')    return <rect    {...shared} {...(shape.props as React.SVGProps<SVGRectElement>)} />;
        if (shape.el === 'path')    return <path    {...shared} {...(shape.props as React.SVGProps<SVGPathElement>)} />;
        if (shape.el === 'circle')  return <circle  {...shared} {...(shape.props as React.SVGProps<SVGCircleElement>)} />;
        return null;
      })}
    </g>
  );
}

// ── Main Component ────────────────────────────────────────────

interface MuscleBodyMapProps {
  compact?: boolean;
}

export default function MuscleBodyMap({ compact = false }: MuscleBodyMapProps) {
  const [selected, setSelected] = useState<{ muscle: MuscleGroup; label: string } | null>(null);

  const muscleStates = useLiveQuery(() => db.muscleStates.toArray(), []) ?? [];
  const stateMap = new Map<MuscleGroup, MuscleState>(muscleStates.map((s) => [s.muscle, s]));

  const weeklyVolumeMap = useLiveQuery(async () => {
    const cutoff = new Date(Date.now() - 7 * 86_400_000).toISOString();
    const sets = await db.loggedSets
      .where('loggedAt').aboveOrEqual(cutoff)
      .filter((s) => !s.isWarmup && s.rpe >= 7)
      .toArray();
    const exIds = [...new Set(sets.map((s) => s.exerciseId))];
    const exercises = await db.exercises.bulkGet(exIds);
    const exMap = new Map(exercises.filter(Boolean).map((e) => [e!.id, e!]));
    const vol = new Map<MuscleGroup, number>();
    for (const s of sets) {
      const ex = exMap.get(s.exerciseId);
      if (!ex) continue;
      for (const alloc of ex.muscles) vol.set(alloc.muscle, (vol.get(alloc.muscle) ?? 0) + alloc.fraction);
    }
    return vol;
  }, []) ?? new Map<MuscleGroup, number>();

  const getMuscleColor = useCallback((muscle: MuscleGroup): string => {
    const state = stateMap.get(muscle);
    if (!state) return fatigueToColor(0, false, false);
    return fatigueToColor(computeCurrentFatigue(state), state.isInjured, true);
  }, [stateMap]);

  const selectedState   = selected ? stateMap.get(selected.muscle) : undefined;
  const selectedFatigue = selectedState ? computeCurrentFatigue(selectedState) : 0;
  const selectedVol     = selected ? (weeklyVolumeMap.get(selected.muscle) ?? 0) : 0;

  return (
    <>
      <div className={compact ? '' : 'card'}>
        {/* Legend */}
        <div className="flex items-center justify-between mb-3 px-1">
          <p className="text-xs font-semibold text-ink-muted uppercase tracking-widest">
            Muscle Recovery
          </p>
          <div className="flex items-center gap-3 text-xs text-ink-faint">
            {[['bg-fresh','Fresh'],['bg-partial','Partial'],['bg-fatigued','Fatigued']].map(([cls,lbl]) => (
              <span key={lbl} className="flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full ${cls} inline-block`} /> {lbl}
              </span>
            ))}
          </div>
        </div>

        {/* View labels */}
        <div className="flex text-xs text-ink-faint font-medium uppercase tracking-widest mb-1">
          <span className="flex-1 text-center">Front</span>
          <span className="flex-1 text-center">Back</span>
        </div>

        {/* SVG */}
        <svg viewBox="0 0 240 332" style={{ width: '100%' }} aria-label="Interactive muscle recovery map">
          <defs>
            <clipPath id="clip-front">{bodyShapes(60)}</clipPath>
            <clipPath id="clip-back">{bodyShapes(180)}</clipPath>
          </defs>

          {/* Divider */}
          <line x1={120} y1={8} x2={120} y2={328} stroke="#333" strokeWidth="0.5" strokeDasharray="4 4" />

          {/* Front */}
          <BodyFill cx={60} />
          <g clipPath="url(#clip-front)">
            {FRONT_REGIONS.map((r) => (
              <MuscleRegion
                key={`f-${r.muscle}`}
                region={r}
                color={getMuscleColor(r.muscle)}
                isSelected={selected?.muscle === r.muscle}
                onClick={() => setSelected({ muscle: r.muscle, label: r.label })}
              />
            ))}
          </g>
          <BodyOutline cx={60} />

          {/* Back */}
          <BodyFill cx={180} />
          <g clipPath="url(#clip-back)">
            {BACK_REGIONS.map((r) => (
              <MuscleRegion
                key={`b-${r.muscle}`}
                region={r}
                color={getMuscleColor(r.muscle)}
                isSelected={selected?.muscle === r.muscle}
                onClick={() => setSelected({ muscle: r.muscle, label: r.label })}
              />
            ))}
          </g>
          <BodyOutline cx={180} />
        </svg>

        <p className="text-center text-xs text-ink-faint mt-2">
          Tap any muscle for recovery details
        </p>
      </div>

      {selected && (
        <DetailSheet
          muscle={selected.muscle}
          label={selected.label}
          state={selectedState}
          fatigue={selectedFatigue}
          weeklyVol={selectedVol}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}
